import Button from '@alltrails/shared/denali/components/Button';
import Typography from '@alltrails/shared/denali/components/Typography';
import { defineMessages, FormattedMessage } from '@alltrails/shared/react-intl';
import { MEDIA_QUERY_MOBILE_DOWN } from 'utils/constants/breakpoints';
import { wrapUrlSafe } from 'utils/language_support_util';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { lazy, Suspense } from 'react';
import { NoSsr } from '@material-ui/core';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import useCurrencyFormatter from 'hooks/useCurrencyFormatter';
import * as styles from './styles/styles.module.scss';
import headerImgSrc from '../../../../assets/images/welcome/header/couple-and-dog.jpg';
import headerImgSrcWebp from '../../../../assets/images/welcome/header/couple-and-dog.webp';
import headerImgSrcAvif from '../../../../assets/images/welcome/header/couple-and-dog.avif';
import headerImgMobileSrc from '../../../../assets/images/welcome/header/couple-and-dog-mobile.jpg';
import headerImgMobileSrcWebp from '../../../../assets/images/welcome/header/couple-and-dog-mobile.webp';
import headerImgMobileSrcAvif from '../../../../assets/images/welcome/header/couple-and-dog-mobile.avif';

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

const WelcomeHeader = () => {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const WELCOME_HEADER_STRINGS = defineMessages({
    IMAGE_ALT: {
      defaultMessage: 'A couple on a hike with their dog.'
    },
    TRY_PLUS_BUTTON: {
      defaultMessage: 'Start your free trial'
    }
  });
  const {
    formattedDefaultMessages: { IMAGE_ALT, TRY_PLUS_BUTTON }
  } = useFormatMessage(WELCOME_HEADER_STRINGS);

  const { plan, currencyCode } = __AT_DATA__;
  const formatter = useCurrencyFormatter(currencyCode);

  const annualCost = Number(plan.priceTotal);
  const monthlyCost = Number(plan.priceMonth);

  return (
    <div className={styles.container}>
      <picture>
        <source media={MEDIA_QUERY_MOBILE_DOWN} srcSet={headerImgMobileSrcAvif} type="image/avif" />
        <source media={MEDIA_QUERY_MOBILE_DOWN} srcSet={headerImgMobileSrcWebp} type="image/webp" />
        <source media={MEDIA_QUERY_MOBILE_DOWN} srcSet={headerImgMobileSrc} type="image/jpg" />
        <source srcSet={headerImgSrcAvif} type="image/avif" />
        <source srcSet={headerImgSrcWebp} type="image/webp" />
        <img src={headerImgSrc} alt={IMAGE_ALT} className={styles.image} />
      </picture>
      <div className={styles.innerContainer}>
        <h1 className={styles.header}>
          <FormattedMessage defaultMessage="All your local trails in one app" />
        </h1>
        <p className={styles.description}>
          <FormattedMessage defaultMessage="Find walking paths near you with AllTrails, your companion that leads the way outside. Try AllTrails+ for extra tools like offline maps and more." />
        </p>
        <div className={styles.buttonContainer}>
          <Button
            text={TRY_PLUS_BUTTON}
            linkInfo={{
              href: user ? wrapUrlSafe('/plus/checkout', languageRegionCode) : wrapUrlSafe('/signup?returnTo=%2Fplus%2Fcheckout', languageRegionCode)
            }}
            testId="welcome-header-try-pro"
            variant="primary"
          />
          <Typography variant="text100">
            <FormattedMessage
              defaultMessage="Then only {annualCost} a year (that’s {monthlyCost} a month)"
              values={{
                annualCost: formatter.format(annualCost),
                monthlyCost: formatter.format(monthlyCost)
              }}
            />
          </Typography>
        </div>
        <div className={styles.chevronContainer}>
          <NoSsr>
            <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
              <ChevronDownBouncing />
            </Suspense>
          </NoSsr>
        </div>
      </div>
    </div>
  );
};

export default WelcomeHeader;
